import { FC, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { getApiUrl } from "../../utils/getApiUrl"
import { IServiceOrderShort } from "../../types/content"
import moment from "moment/moment"
import clsx from "clsx"
import { WorkListIcon } from "../../icons/WorkListIcon"
import { ArrowBoldRightIcon } from "../../icons/ArrowBoldRightIcon"
import useWindowSize from "../../hooks/useWindowSize"
import styles from "./OrderItem.module.scss"
import CoordinationStatusLabel from "../Coordinations/CoordinationStatusLabel/CoordinationStatusLabel"
import StarsRating from "../Assets/StarsRating/StarsRating"
import { ArchiveListIcon } from "../../icons/ArchiveListIcon"
import { getFileIco } from "../../utils/helpers"
import { usePdfCheckReqMutation, usePdfCreateReqMutation } from "../../redux/api/content"
import { useAppSelector } from "../../hooks"
import { selectUser } from "../../redux/slice/auth"
import Label from "../Assets/Label/Label"
import LabelInfo from "../Assets/LabelInfo/LabelInfo"

interface Props extends IServiceOrderShort {
  clickFcn?: () => void
  ratingFn?: () => void
  layout?: "history" | "active"
}

const OrderItem: FC<Props> = ({
  photos,
  name,
  path_name,
  created_at,
  clickFcn,
  count,
  layout,
  ratingFn,
  id,
  project_id,
  status,
  hasReview,
  message_counts,
  approval_status,
  score,
  finished_at,
  number,
  status_alias,
  status_info,
  ordered_by,
  orderer_info,
}) => {
  const { t } = useTranslation("translation", { keyPrefix: `interface` })
  const { t: tStatus } = useTranslation("translation", { keyPrefix: `interface.orderStatus` })
  const { isDesktop } = useWindowSize()

  const [iconVisible, iconSrc] = getFileIco("pdf")

  const user = useAppSelector(selectUser)

  const [pdfCheckReq, pdfCheckReqRData] = usePdfCheckReqMutation()
  const [pdfCreateReq, pdfCreateReqRData] = usePdfCreateReqMutation()

  const [wasReview, setWasReview] = useState<boolean>(false)
  const [pdfInfo, setPdfInfo] = useState<any>({ file_link: null, in_progress: false })

  const { ordersList } = useSelector((state: any) => state.ordersList)

  useEffect(() => {
    const order = ordersList.find((el: any) => el.id === id)
    if (order) {
      setWasReview(order.hasReview)
    }
  })

  // Запуск генерации отчёта
  const generateReport = (e: any) => {
    e.stopPropagation()
    setPdfInfo({ file_link: null, in_progress: true })
    pdfCreateReq({ id: id, project_id: project_id }).catch(() => setPdfInfo({ file_link: null, in_progress: false }))
  }

  const leaveFeedback = useMemo(() => {
    if (layout === "active" || !ratingFn || wasReview || hasReview || status !== 1) {
      return null
    }
    if (user?.id !== ordered_by) return null

    return (
      <button
        className={styles.ratingBtn}
        onClick={(e) => {
          e.stopPropagation()
          ratingFn()
        }}
      >
        {isDesktop ? t("giveFeedback") : t("giveRating")}
        <span className={styles.ratingBtn__iconWrap}>
          <ArrowBoldRightIcon />
        </span>
      </button>
    )
  }, [layout, isDesktop, hasReview, wasReview, ordered_by, user])

  const LabelOrder = useMemo(() => {
    if (!status_info?.alias) return null
    const statusInfoName = status_info.name
    switch (status_info.alias) {
      case "suspended":
        return <LabelInfo txt={statusInfoName} color={"danger"} />
      case "cancelled":
        return <LabelInfo txt={statusInfoName} color={"danger"} />
      case "resolved":
        return <LabelInfo txt={statusInfoName} color={"success"} />
      default:
        return <LabelInfo txt={statusInfoName} />
    }
  }, [layout, status_info])

  useEffect(() => {
    if (typeof window.Echo === "undefined" || !user?.id) return

    const channelPrivate = window.Echo.private(`privateUser.${user?.id}`)

    channelPrivate?.listen(".pdfCreatedOrder", (event: any) => {
      if (event.id === id) {
        setPdfInfo({ file_link: event?.file_link, in_progress: false })
      }
    })

    return () => {
      channelPrivate.stopListening(`.pdfCreatedOrder`)
    }
  }, [user?.id, id])

  return (
    <div className={styles.orderItem} onClick={clickFcn}>
      <div className={styles.inner}>
        <div className={styles["content-container"]}>
          <div className={clsx(styles.imgWrap, !photos?.length && styles["imgWrap--empty"])}>
            {photos?.length > 0 ? (
              <img src={`${getApiUrl()}/public/photo/preview/${photos[0]}?project_id=${project_id}`} alt="" />
            ) : (
              <WorkListIcon />
            )}
          </div>
          <div className={styles.info}>
            {(finished_at || score) && layout === "history" && (
              <div className={styles.completedAndRate}>
                {finished_at && layout === "history" && (
                  <div className={styles.completed}>
                    <span>{t("done")}</span> {moment(Number(finished_at) * 1000).format("DD MMMM YYYY HH:mm")}
                  </div>
                )}
                {score && (
                  <div className={styles.rate}>
                    {["", "", "", "", ""].map((_: any, i: number) => (
                      <svg
                        key={i}
                        width="21"
                        height="20"
                        viewBox="0 0 21 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fill={i + 1 <= score ? "#F2A60F" : "#E0E7EB"}
                          d="M10.1941 0.582386C10.4814 -0.0404822 11.3667 -0.0404829 11.654 0.582385L14.0241 5.72072C14.1412 5.97457 14.3818 6.14936 14.6594 6.18228L20.2786 6.84853C20.9598 6.92929 21.2333 7.77121 20.7297 8.23693L16.5753 12.0788C16.37 12.2687 16.2781 12.5515 16.3326 12.8257L17.4354 18.3758C17.5691 19.0485 16.8529 19.5689 16.2544 19.2338L11.3167 16.4699C11.0728 16.3334 10.7754 16.3334 10.5314 16.4699L5.59377 19.2338C4.99523 19.5689 4.27905 19.0485 4.41273 18.3758L5.51553 12.8257C5.57001 12.5515 5.47812 12.2687 5.27287 12.0788L1.11842 8.23693C0.614821 7.77121 0.888379 6.92929 1.56954 6.84853L7.18878 6.18228C7.4664 6.14936 7.70697 5.97457 7.82406 5.72072L10.1941 0.582386Z"
                        />
                      </svg>
                    ))}
                  </div>
                )}
              </div>
            )}
            <p className={styles.ordererName}>{orderer_info.name}</p>
            <p className={styles.name}>
              {name} {count > 1 ? <span>x{count}</span> : null}
            </p>
            {path_name?.length > 0 ? <span className={styles.description}>{path_name}</span> : null}
            {isDesktop && (leaveFeedback || layout === "history") ? (
              <div className={styles.btnBlock}>
                {leaveFeedback}
                {layout === "history" ? (
                  pdfInfo?.file_link ? (
                    <a
                      href={`${pdfInfo?.file_link}`}
                      rel="noreferrer"
                      className={clsx(styles.pdfBlock__download)}
                      download
                      onClick={(e: any) => {
                        e.stopPropagation()
                      }}
                    >
                      <img src={`${iconSrc ?? ""}`} alt="" /> {t("saveReportPdf")}
                    </a>
                  ) : (
                    <button
                      type="button"
                      className={clsx(styles.pdfBlock__btn, pdfInfo?.in_progress ? styles.loading : "")}
                      onClick={generateReport}
                    >
                      {pdfInfo?.in_progress ? t("reportGenerating") : t("generateReport")}
                      {pdfInfo?.in_progress ? (
                        <div className={clsx(styles.pdfBlock__loaderWp)}>
                          <img
                            className={clsx(styles.btn__loader, "btn__loader")}
                            src={"/img/loader_blue.png"}
                            alt="Loading..."
                          />
                        </div>
                      ) : (
                        ArchiveListIcon()
                      )}
                    </button>
                  )
                ) : null}
              </div>
            ) : null}
            {/*{status === -1 && <div className={styles.canceled}>{t("canceledOrder")}</div>}*/}
            {(!!approval_status && status === 0) || status_alias ? (
              <div className={styles.labelList}>
                {!!approval_status && status === 0 && (
                  <CoordinationStatusLabel className={styles["coordination-status-label"]} status={approval_status} />
                )}
                {LabelOrder}
              </div>
            ) : null}
          </div>
        </div>
        <div className={clsx(styles.time, layout === "history" && styles["time-for-history"])}>
          {moment(created_at).format("kk:mm")}
        </div>
        <span className={styles.number}>#{number}</span>
        {!isDesktop && leaveFeedback}
      </div>
      {!!message_counts && <span className={`${styles.message}`}>{message_counts}</span>}
    </div>
  )
}

export default OrderItem
